import { useCallback, useMemo, memo } from 'react';
import { useDispatch } from 'react-redux';
import ColorManager from '../../../common/d3/ColorManager';
import { messages } from '../../../i18n';
import { ColorPickerRow, ColorSeriesLink } from '../../../ui/colors';
import { LineChartUtils } from '../../charts/LineChartUtils/LineChartUtils';
import {
  StyledColorSettings,
  useAddColorButtonSx,
} from './color-settings.styles';
import Discover from '../../../common/redux/actions/DiscoverActions';
import { assign, get, isEmpty, kebabCase } from 'lodash';
import { DisableableDiv, useDiscoverTheme } from '../../../common/emotion';
import { ButtonLink } from '../../../ui/button';
import { useColorSettings } from './color-settings.hook';
import { ColorGroupSetting } from './color-group-setting';
import {
  IColorSetting,
  IColorSettings,
  IUseColorSettings,
} from './color-settings.interfaces';
import {
  useDiscoveryIdSelector,
  useOpenVizSelector,
} from '../../../common/redux/selectors/viz-selector.hook';
import { Viz } from '../../VizUtil';

export const ColorSetting = memo<IColorSetting>(
  ({ item, vizId, customColors }) => {
    const { label, shape, info } = item;
    const discoveryId = useDiscoveryIdSelector();

    const viz = useOpenVizSelector({ discoveryId });
    const theme = useDiscoverTheme();
    const itemLabel = useMemo(() => {
      if (shape === 'LINE') {
        return LineChartUtils.getColorKey(info);
      }
      return label;
    }, [label, shape, info]);

    const dispatch = useDispatch();
    const onColorChange = useCallback(
      newColor => {
        const newColors = assign({}, customColors, { [itemLabel]: newColor });
        dispatch(Discover.updateCustomColors(vizId, newColors));
      },
      [customColors, itemLabel, dispatch, vizId],
    );

    const color = ColorManager.getColor(vizId, itemLabel, theme);
    const selColor = get(customColors, [itemLabel], color);
    const displayName = Viz.replaceNameWithDisplayName({
      viz,
      name: label,
    });
    return (
      <ColorPickerRow
        key={item.label}
        label={displayName}
        selectedColor={selColor}
        onSelect={onColorChange}
      />
    );
  },
);

export const ColorSettings = memo<IColorSettings>(
  ({ chartType, legendData, vizId, customColors }) => {
    const hasCustomColors = !isEmpty(customColors);
    const addColorGroupButtonStyles = useAddColorButtonSx();
    const {
      resetColors,
      addColorGroup,
      legendGroupData = [],
      isAddColorGroupDisabled,
    }: IUseColorSettings = useColorSettings(vizId, legendData);
    const isWaterfall = chartType === 'waterfall';

    if (chartType === 'pivot') {
      return null;
    }

    return (
      <StyledColorSettings className='target-settings'>
        <div className='viz-formatting-header'>
          <DisableableDiv className='viz-formatting-header-left'>
            {messages.formatting.colors}
          </DisableableDiv>
          {isWaterfall && (
            <ButtonLink
              className='viz-formatting-header-right'
              onClick={addColorGroup}
              sx={addColorGroupButtonStyles}
              disabled={isAddColorGroupDisabled}
            >
              {messages.formatting.addColorGroup}
            </ButtonLink>
          )}
        </div>
        <div className='viz-formatting-form-group'>
          {isWaterfall &&
            legendGroupData.map((item, idx) => {
              return (
                <ColorGroupSetting
                  key={`color-group-setting-${kebabCase(item.id)}`}
                  customColors={customColors}
                  item={item}
                  vizId={vizId}
                  toIndex={idx}
                />
              );
            })}
          {legendData.map(item => {
            return (
              <ColorSetting
                key={`color-setting-${kebabCase(item.label)}`}
                customColors={customColors}
                item={item}
                vizId={vizId}
              />
            );
          })}
          <div className='viz-formatting-links'>
            <ColorSeriesLink disabled={!hasCustomColors} onClick={resetColors}>
              {messages.formatting.resetToDefaultColors}
            </ColorSeriesLink>
          </div>
        </div>
      </StyledColorSettings>
    );
  },
);
